import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { defineModel } from "../types";

// MuAPI's no-video endpoint only accepts these. The Zod enum is wider for Replicate.
const MUAPI_ASPECT_RATIOS = ["16:9", "9:16", "4:3", "3:4"];

const COST_PER_SECOND = 0.3 * 1_000_000 * (1 + BRAND_MARKUP);

// Replicate pricing: non-video-in for image+audio refs, video-in when video refs present
const REPLICATE_OMNI_COST_PER_SECOND = 0.17 * 1_000_000 * (1 + BRAND_MARKUP);
const REPLICATE_OMNI_VIDEO_IN_COST_PER_SECOND =
  0.29 * 1_000_000 * (1 + BRAND_MARKUP);

const seedance2OmniParams = z
  .object({
    aspectRatio: z
      .enum(["21:9", "16:9", "4:3", "1:1", "3:4", "9:16"])
      .default("9:16"),
    duration: z
      .enum(["4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15"])
      .default("5"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const seedance2Omni = defineModel({
  key: "seedance-2-omni",
  stepType: "video",
  spec: {
    label: "Seedance 2.0 Omni-Reference",
    description:
      "Reference-based video generation using images, videos, and audio for visual consistency",
    icon: "seedance-icon.png",
    tags: ["video-gen", "reference"],
    featured: true,
    info: "Use @image1, @video1, @audio1 in your prompt to reference attached media. Supports up to 3 images, 3 videos, and 1 audio clip.",
    features: {
      aspectRatio: true,
      duration: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image" as const,
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Reference Image 1",
          slot: "ref_image_1",
          group: "ref_images",
          required: false,
        },
        {
          type: "image" as const,
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Reference Image 2",
          slot: "ref_image_2",
          group: "ref_images",
          required: false,
        },
        {
          type: "image" as const,
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Reference Image 3",
          slot: "ref_image_3",
          group: "ref_images",
          required: false,
        },
        {
          type: "video" as const,
          allowedFormats: ["video/mp4", "video/quicktime", "video/webm"],
          label: "Reference Video 1",
          slot: "ref_video_1",
          group: "ref_videos",
          required: false,
        },
        {
          type: "video" as const,
          allowedFormats: ["video/mp4", "video/quicktime", "video/webm"],
          label: "Reference Video 2",
          slot: "ref_video_2",
          group: "ref_videos",
          required: false,
        },
        {
          type: "video" as const,
          allowedFormats: ["video/mp4", "video/quicktime", "video/webm"],
          label: "Reference Video 3",
          slot: "ref_video_3",
          group: "ref_videos",
          required: false,
        },
        {
          type: "audio" as const,
          allowedFormats: ["audio/mpeg", "audio/wav"],
          label: "Reference Audio 1",
          slot: "ref_audio_1",
          group: "ref_audio",
          required: false,
        },
      ],
      aspectRatio: {
        items: [
          { label: "16:9", value: "16:9" },
          { label: "9:16", value: "9:16" },
          { label: "1:1", value: "1:1" },
          { label: "4:3", value: "4:3" },
          { label: "3:4", value: "3:4" },
          { label: "21:9", value: "21:9" },
        ],
      },
      duration: {
        items: [
          { label: "4s", value: "4" },
          { label: "5s", value: "5" },
          { label: "8s", value: "8" },
          { label: "10s", value: "10" },
          { label: "15s", value: "15" },
        ],
      },
    },
    params: seedance2OmniParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/bytedance/seedance-2.0/reference-to-video",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 302_400 }],
  },
  providers: [
    {
      provider: "replicate",
      modelId: "bytedance/seedance-2.0",
      estimateCost(params) {
        const duration = Number(params.duration) || 5;
        const hasVideoRefs = (params.attachments ?? []).some(
          (att) => att.slot.startsWith("ref_video_") && att.data,
        );
        const perSecond = hasVideoRefs
          ? REPLICATE_OMNI_VIDEO_IN_COST_PER_SECOND
          : REPLICATE_OMNI_COST_PER_SECOND;
        return Math.ceil(perSecond * duration);
      },
      estimateCostPerSecond(params) {
        const hasVideoRefs = (params.attachments ?? []).some(
          (att) => att.slot.startsWith("ref_video_") && att.data,
        );
        return hasVideoRefs
          ? REPLICATE_OMNI_VIDEO_IN_COST_PER_SECOND
          : REPLICATE_OMNI_COST_PER_SECOND;
      },
      costPerSecond: REPLICATE_OMNI_COST_PER_SECOND,
      toPayload(params) {
        const imageUrls = (params.attachments ?? [])
          .filter((att) => att.slot.startsWith("ref_image_"))
          .map((att) => att.data)
          .filter((url): url is string => Boolean(url));

        const videoUrls = (params.attachments ?? [])
          .filter((att) => att.slot.startsWith("ref_video_"))
          .map((att) => att.data)
          .filter((url): url is string => Boolean(url));

        const audioUrls = (params.attachments ?? [])
          .filter((att) => att.slot.startsWith("ref_audio_"))
          .map((att) => att.data)
          .filter((url): url is string => Boolean(url));

        // Rewrite MuAPI-style @image1 refs to Replicate-style [Image1]
        const prompt = (params.prompt || "")
          .replace(/@image(\d+)/gi, (_, num) => `[Image${num}]`)
          .replace(/@audio(\d+)/gi, (_, num) => `[Audio${num}]`)
          .replace(/@video(\d+)/gi, (_, num) => `[Video${num}]`);

        // Replicate's seedance-2.0 (Normal) Cog has a bug where reference_videos
        // fails with "reference_video must be provided as a web url". The Fast
        // variant handles video refs correctly. Force Fast when video refs are
        // present. Remove this override when Replicate fixes the Normal Cog.
        const hasVideoRefs = videoUrls.length > 0;
        const model = hasVideoRefs
          ? ("bytedance/seedance-2.0-fast" as const)
          : ("bytedance/seedance-2.0" as const);

        return {
          model,
          input: {
            prompt: prompt || undefined,
            ...(imageUrls.length > 0
              ? { reference_images: imageUrls.slice(0, 9) }
              : {}),
            ...(hasVideoRefs
              ? { reference_videos: videoUrls.slice(0, 3) }
              : {}),
            ...(audioUrls.length > 0
              ? { reference_audios: audioUrls.slice(0, 3) }
              : {}),
            resolution: "720p" as const,
            duration: Number(params.duration),
            aspect_ratio: params.aspectRatio,
            generate_audio: true,
          },
        };
      },
    },
    {
      // MuAPI fallback: "no-video" endpoint — video refs are stripped (degraded fallback).
      // Image + audio refs still work. If video refs were the primary input,
      // the generation will proceed without them — better than failing entirely.
      provider: "muapi",
      modelId: "seedance-2-omni-reference-no-video",
      estimateCost(params) {
        const duration = Number(params.duration) || 5;
        return Math.ceil(COST_PER_SECOND * duration);
      },
      costPerSecond: COST_PER_SECOND,
      toPayload(params) {
        const imageUrls = (params.attachments ?? [])
          .filter((att) => att.slot.startsWith("ref_image_"))
          .map((att) => att.data)
          .filter(Boolean) as string[];

        const audioUrls = (params.attachments ?? [])
          .filter((att) => att.slot.startsWith("ref_audio_"))
          .map((att) => att.data)
          .filter(Boolean) as string[];

        // Video refs intentionally omitted — MuAPI's no-video endpoint doesn't support them

        if (!MUAPI_ASPECT_RATIOS.includes(params.aspectRatio)) {
          throw new ValidationError(
            `Aspect ratio '${params.aspectRatio}' isn't available for this generation. Supported ratios: 16:9, 9:16, 4:3, 3:4.`,
          );
        }

        return {
          endpoint: "seedance-2-omni-reference-no-video" as const,
          prompt: params.prompt || undefined,
          ...(imageUrls.length > 0 ? { images_list: imageUrls } : {}),
          ...(audioUrls.length > 0 ? { audio_files: audioUrls } : {}),
          aspect_ratio: params.aspectRatio,
          duration: Number(params.duration),
          remove_watermark: true,
        };
      },
    },
  ],
});
